package one.healthium.insights.ui.personal_mealplan

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.composables.core.SheetDetent.Companion.FullyExpanded
import com.composables.core.SheetDetent.Companion.Hidden
import com.skydoves.compose.stability.runtime.TraceRecomposition
import ifaandroid.feature.content.insights.generated.resources.Res
import ifaandroid.feature.content.insights.generated.resources.bg_mealplan_topbar
import ifaandroid.feature.content.insights.generated.resources.ic_refresh_24
import loco.CONTENT_MEAL_PLAN_PERSONAL_LABEL
import loco.CONTENT_PERSONAL_MEAL_PLAN_ADJUST_PREFERENCES
import loco.CONTENT_PERSONAL_MEAL_PLAN_BACK
import loco.CONTENT_PERSONAL_MEAL_PLAN_REFRESH
import loco.CONTENT_PERSONAL_MEAL_PLAN_SHOPPING_LIST
import loco.CONTENT_PERSONAL_MEAL_PLAN_TRY_AGAIN
import loco.CONTENT_SHOPPING_LIST_BUTTON
import kotlinx.coroutines.delay
import one.healthium.insights.domain.models.ActionModel
import one.healthium.insights.domain.models.MealPlanResultModel
import one.healthium.insights.domain.models.RecipeModel
import one.healthium.insights.domain.models.toLogMealAction
import one.healthium.insights.ui.content.ContentEventDelegate
import one.healthium.ui.compose.components.AppBackNavigationButton
import one.healthium.ui.compose.components.AppIcons
import one.healthium.ui.compose.components.AppLoadingScreen
import one.healthium.ui.compose.components.rememberAppModalBottomSheetState
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.components.AppTopBar
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.black50
import one.healthium.ui.compose.theme.white
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun PersonalMealPlanScreen(
    modifier: Modifier = Modifier,
    eventDelegate: ContentEventDelegate,
) {
    val viewModel = koinViewModel<PersonalMealPlanViewModel>()
    val state by viewModel.state().collectAsState()

    PersonalMealPlanScreenContent(
        modifier = modifier,
        state = state,
        onBackClicked = eventDelegate::onBackClicked,
        onActionClicked = eventDelegate::onActionClicked,
        onRetry = viewModel::retry,
        onDaySelected = viewModel::selectDay,
        onRecipeClicked = viewModel::onRecipeClicked,
    )

    val selectedRecipe = state.selectedRecipe
    var displayedRecipe by remember { mutableStateOf<SelectedRecipe?>(null) }
    if (selectedRecipe != null) {
        displayedRecipe = selectedRecipe
    }

    val sheetState = rememberAppModalBottomSheetState(initialDetent = Hidden)

    LaunchedEffect(selectedRecipe) {
        if (selectedRecipe != null) {
            delay(100)
            sheetState.targetDetent = FullyExpanded
        } else {
            sheetState.targetDetent = Hidden
        }
    }

    displayedRecipe?.let { selected ->
        RecipeBottomSheet(
            sheetState = sheetState,
            recipe = selected.recipe,
            mealType = selected.mealType,
            onDismissed = viewModel::dismissRecipe,
            onLogMeal = { eventDelegate.onActionClicked(it) },
        )
    }
}

@Composable
private fun PersonalMealPlanScreenContent(
    modifier: Modifier = Modifier,
    state: PersonalMealPlanUiState,
    onBackClicked: () -> Unit,
    onActionClicked: (ActionModel) -> Unit,
    onRetry: () -> Unit,
    onDaySelected: (Int) -> Unit,
    onRecipeClicked: (RecipeModel, String) -> Unit,
) {
    when (val result = state.result) {
        is MealPlanResultModel.Ready -> ReadyContent(
            modifier = modifier,
            result = result,
            selectedDayIndex = state.selectedDayIndex,
            onBackClicked = onBackClicked,
            onActionClicked = onActionClicked,
            onDaySelected = onDaySelected,
            onRecipeClicked = onRecipeClicked,
        )

        is MealPlanResultModel.Processing -> ProcessingContent(
            modifier = modifier,
            result = result,
            onBackClicked = onBackClicked,
        )

        is MealPlanResultModel.Error -> ErrorContent(
            modifier = modifier,
            result = result,
            onBackClicked = onBackClicked,
            onActionClicked = onActionClicked,
            onRetry = onRetry,
        )

        null -> {
            AppLoadingScreen(
                modifier = modifier,
                visible = state.loading,
            )
        }
    }
}

// region Ready Content
@Composable
private fun ReadyContent(
    modifier: Modifier = Modifier,
    result: MealPlanResultModel.Ready,
    selectedDayIndex: Int,
    onBackClicked: () -> Unit,
    onActionClicked: (ActionModel) -> Unit,
    onDaySelected: (Int) -> Unit,
    onRecipeClicked: (RecipeModel, String) -> Unit,
) {
    val mealPlan = result.mealPlan
    val days = mealPlan.days
    val clampedIndex = selectedDayIndex.coerceIn(0, (days.size - 1).coerceAtLeast(0))
    val selectedDay = days.getOrNull(clampedIndex)

    Box(
        modifier = modifier
            .fillMaxSize()
            .background(AppTheme.colors.windowBackground)
            .testTag("personal_meal_plan_screen"),
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState()),
        ) {
            // Hero image
            Image(
                painter = painterResource(Res.drawable.bg_mealplan_topbar),
                contentDescription = null,
                contentScale = ContentScale.Crop,
                modifier = Modifier
                    .fillMaxWidth()
                    .height(256.dp)
                    .clip(RoundedCornerShape(bottomStart = 16.dp, bottomEnd = 16.dp)),
            )

            Spacer(modifier = Modifier.height(16.dp))

            // Day selector
            if (days.isNotEmpty()) {
                DaySelector(
                    dayCount = days.size,
                    selectedIndex = clampedIndex,
                    onDaySelected = onDaySelected,
                )
            }

            Spacer(modifier = Modifier.height(16.dp))

            // White card
            if (selectedDay != null) {
                MealPlanCard(
                    dayIndex = clampedIndex,
                    day = selectedDay,
                    onRecipeClicked = onRecipeClicked,
                    onLogMeal = { recipe ->
                        onActionClicked(recipe.toLogMealAction())
                    },
                )
            }

            Spacer(modifier = Modifier.height(160.dp))
        }

        // Pinned action buttons — always on top
        Row(
            modifier = Modifier
                .statusBarsPadding()
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 10.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            Box(
                modifier = Modifier
                    .testTag("content_close")
                    .size(36.dp)
                    .clip(CircleShape)
                    .clickable(onClick = onBackClicked)
                    .background(black50),
                contentAlignment = Alignment.Center,
            ) {
                Icon(
                    painter = painterResource(AppIcons.icNavigationBack),
                    tint = white,
                    contentDescription = stringResource(loco.Res.string.CONTENT_PERSONAL_MEAL_PLAN_BACK),
                )
            }

            Spacer(modifier = Modifier.weight(1f))

            val refreshDescription = stringResource(loco.Res.string.CONTENT_PERSONAL_MEAL_PLAN_REFRESH)
            Box(
                modifier = Modifier
                    .size(36.dp)
                    .clip(CircleShape)
                    .clickable(onClick = { onActionClicked(ActionModel.UpdateFoodPreferences) })
                    .background(black50)
                    .semantics { contentDescription = refreshDescription },
                contentAlignment = Alignment.Center,
            ) {
                Icon(
                    painter = painterResource(Res.drawable.ic_refresh_24),
                    tint = white,
                    contentDescription = null,
                )
            }

            if (mealPlan.shoppingList != null) {
                val shoppingDescription = stringResource(loco.Res.string.CONTENT_PERSONAL_MEAL_PLAN_SHOPPING_LIST)
                Row(
                    modifier = Modifier
                        .clip(CircleShape)
                        .clickable(onClick = { mealPlan.shoppingList.let { onActionClicked(ActionModel.ShoppingList(it)) } })
                        .background(black50, CircleShape)
                        .padding(horizontal = 12.dp, vertical = 9.dp)
                        .semantics { contentDescription = shoppingDescription },
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Icon(
                        painter = painterResource(AppIcons.icCart),
                        tint = white,
                        contentDescription = null,
                    )
                    Spacer(Modifier.width(4.dp))
                    Text(
                        text = stringResource(loco.Res.string.CONTENT_SHOPPING_LIST_BUTTON),
                        style = AppTheme.textStyles.secondary_14_500,
                        color = white,
                    )
                }
            }
        }
    }
}
// endregion

// region Processing Content
@TraceRecomposition
@Composable
private fun ProcessingContent(
    modifier: Modifier = Modifier,
    result: MealPlanResultModel.Processing,
    onBackClicked: () -> Unit,
) {
    AppScaffold(
        modifier = modifier.testTag("personal_meal_plan_screen"),
        topBar = {
            AppTopBar(
                title = {
                    Text(stringResource(loco.Res.string.CONTENT_MEAL_PLAN_PERSONAL_LABEL))
                },
                navigationIcon = {
                    AppBackNavigationButton(
                        modifier = Modifier.testTag("content_close"),
                        onClick = onBackClicked,
                    )
                },
            )
        }
    ) {
        Box(
            modifier = Modifier.padding(it).fillMaxSize(),
            contentAlignment = Alignment.Center,
        ) {
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(16.dp),
            ) {
                CircularProgressIndicator(
                    modifier = Modifier.size(48.dp),
                )
                Text(
                    text = result.message,
                    style = AppTheme.textStyles.secondary_16_500,
                    color = AppTheme.colors.text100,
                )
            }
        }
    }
}
// endregion

// region Error Content
@Composable
private fun ErrorContent(
    modifier: Modifier = Modifier,
    result: MealPlanResultModel.Error,
    onBackClicked: () -> Unit,
    onActionClicked: (ActionModel) -> Unit,
    onRetry: () -> Unit,
) {
    AppScaffold(
        modifier = modifier.testTag("personal_meal_plan_screen"),
        topBar = {
            AppTopBar(
                title = {
                    Text(stringResource(loco.Res.string.CONTENT_MEAL_PLAN_PERSONAL_LABEL))
                },
                navigationIcon = {
                    AppBackNavigationButton(
                        modifier = Modifier.testTag("content_close"),
                        onClick = onBackClicked,
                    )
                },
            )
        }
    ) {
        Box(
            modifier = Modifier.padding(it).fillMaxSize(),
            contentAlignment = Alignment.Center,
        ) {
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(16.dp),
            ) {
                Text(
                    text = result.message,
                    style = AppTheme.textStyles.secondary_16_500,
                    color = AppTheme.colors.text100,
                )
                when (result.action) {
                    "retry" -> Button(onClick = onRetry) {
                        Text(stringResource(loco.Res.string.CONTENT_PERSONAL_MEAL_PLAN_TRY_AGAIN))
                    }

                    "adjust_preferences" -> Button(
                        onClick = { onActionClicked(ActionModel.UpdateFoodPreferences) }
                    ) {
                        Text(stringResource(loco.Res.string.CONTENT_PERSONAL_MEAL_PLAN_ADJUST_PREFERENCES))
                    }
                }
            }
        }
    }
}
// endregion

// --- Previews ---

@Preview(showBackground = true)
@Composable
private fun ProcessingContentPreview() {
    AppTheme {
        ProcessingContent(
            result = MealPlanResultModel.Processing(message = "Generating your meal plan..."),
            onBackClicked = {},
        )
    }
}

@Preview(showBackground = true)
@Composable
private fun ErrorContentRetryPreview() {
    AppTheme {
        ErrorContent(
            result = MealPlanResultModel.Error(
                message = "Something went wrong. Please try again.",
                action = "retry",
            ),
            onBackClicked = {},
            onActionClicked = {},
            onRetry = {},
        )
    }
}

@Preview(showBackground = true)
@Composable
private fun ErrorContentAdjustPreferencesPreview() {
    AppTheme {
        ErrorContent(
            result = MealPlanResultModel.Error(
                message = "We couldn't generate a plan with your current preferences.",
                action = "adjust_preferences",
            ),
            onBackClicked = {},
            onActionClicked = {},
            onRetry = {},
        )
    }
}
